import {
  OrderType,
  PaymentMethod,
  PaymentTypeEnum,
  ProviderEnum,
  TimeSpan,
} from "api";
import { OrderStatusEnum } from "api/models/order-status-enum";

export const ORDER_STATUS_CONFIG = {
  [OrderStatusEnum.delivered]: {
    label: "Delivered",
    color: "bg-green-500/10 text-green-500 border-green-500/20",
  },
  [OrderStatusEnum.in_progress]: {
    label: "In Progress",
    color: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  },
  [OrderStatusEnum.ready]: {
    label: "Ready",
    color: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  },
  [OrderStatusEnum.rejected]: {
    label: "Rejected",
    color: "bg-red-500/10 text-red-500 border-red-500/20",
  },
  [OrderStatusEnum.refunded]: {
    label: "Refunded",
    color: "bg-orange-500/10 text-orange-500 border-orange-500/20",
  },
  [OrderStatusEnum.pending]: {
    label: "Pending",
    color: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
  },
  [OrderStatusEnum.pick_up_by_driver]: {
    label: "Pickup by Driver",
    color: "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
  },
  [OrderStatusEnum.preorder]: {
    label: "Preorder",
    color: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20",
  },
  [OrderStatusEnum.accept_by_driver]: {
    label: "Accepted by Driver",
    color: "bg-teal-500/10 text-teal-500 border-teal-500/20",
  },
  [OrderStatusEnum.parked]: {
    label: "Parked",
    color: "bg-gray-500/10 text-gray-500 border-gray-500/20",
  },
  [OrderStatusEnum.draft]: {
    label: "Draft",
    color: "bg-slate-500/10 text-slate-500 border-slate-500/20",
  },
} as const;

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.Cash]: "Cash",
  [PaymentMethod.Card]: "Card",
  [PaymentMethod.Voucher]: "Voucher",
  [PaymentMethod.EPassi]: "EPassi",
  [PaymentMethod.Edenred]: "Edenred",
  [PaymentMethod.Munchi]: "Munchi",
  [PaymentMethod.Flatpay]: "Flatpay",
  [PaymentMethod.GiftUp]: "Gift Up",
  [PaymentMethod.MobilePay]: "MobilePay",
  [PaymentMethod.CardManual]: "Card (Manual)",
  [PaymentMethod.EdenredManual]: "Edenred (Manual)",
  [PaymentMethod.EmployeeDiscount]: "Employee Discount",
  [PaymentMethod.LunchVoucher]: "Lunch Voucher",
  [PaymentMethod.EdenredVoucher]: "Edenred Voucher",
  [PaymentMethod.SmartumVoucher]: "Smartum Voucher",
  [PaymentMethod.Invoice]: "Invoice",
  [PaymentMethod.Smartum]: "Smartum",
  [PaymentMethod.GiftCard]: "Gift Card",
  [PaymentMethod.Debit]: "Debit",
  [PaymentMethod.Credit]: "Credit",
  [PaymentMethod.GiftCardManual]: "Gift Card (Manual)",
  [PaymentMethod.WoltBenefit]: "Wolt Benefit",
  [PaymentMethod.Fundis]: "Fundis",
};

export const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  [OrderType.delivery]: "Delivery",
  [OrderType.pick_up]: "Pick Up",
  [OrderType.dine_in]: "Dine In",
};

export const PAYMENT_TYPE_LABELS: Record<PaymentTypeEnum, string> = {
  [PaymentTypeEnum.cash]: "Cash",
  [PaymentTypeEnum.card]: "Card",
};

export const PROVIDER_LABELS: Record<
  Exclude<ProviderEnum, "MunchiKioskV2" | "Kiosk">,
  string
> = {
  [ProviderEnum.Wolt]: "Wolt",
  [ProviderEnum.MunchiOnline]: "Munchi Online",
  [ProviderEnum.MunchiKiosk]: "Munchi Kiosk",
  [ProviderEnum.MunchiPos]: "Munchi POS",
  [ProviderEnum.UberEats]: "Uber Eats",
  [ProviderEnum.Foodora]: "Foodora",
  [ProviderEnum.Bolt]: "Bolt",
  [ProviderEnum.Storefront]: "Storefront",
  [ProviderEnum.WoltStorefront]: "Wolt Storefront",
  [ProviderEnum.MunchiPay]: "Munchi Pay",
};

export const STOREFRONT_PROVIDER = ProviderEnum.Storefront;

export const expandProviderValues = <T extends string>(
  values: T[] | undefined,
): T[] | undefined => {
  if (!values || values.length === 0) return values;
  if (
    values.includes(ProviderEnum.MunchiOnline as T) &&
    !values.includes(STOREFRONT_PROVIDER as T)
  ) {
    return [...values, STOREFRONT_PROVIDER as T];
  }
  return values;
};

export const PAYMENT_METHOD_OPTIONS = Object.entries(PAYMENT_METHOD_LABELS).map(
  ([value, label]) => ({ label, value: value as PaymentMethod }),
);

export const PAYMENT_TYPE_OPTIONS = Object.entries(PAYMENT_TYPE_LABELS).map(
  ([value, label]) => ({ label, value: value as PaymentTypeEnum }),
);

export const PROVIDER_OPTIONS = Object.entries(PROVIDER_LABELS).map(
  ([value, label]) => ({ label, value: value as ProviderEnum }),
);

export const ORDER_STATUS_OPTIONS = Object.entries(ORDER_STATUS_CONFIG)
  .filter(([key]) => key !== OrderStatusEnum.draft)
  .map(([value, config]) => ({
    label: config.label,
    value: value as OrderStatusEnum,
  }));

const TIME_SPAN_LABELS = {
  day: "Today",
  week: "This Week",
  month: "This Month",
  custom: "Custom Range",
} as const;

export function createTimeSpanOptions<
  T extends { day: string; week: string; month: string; custom: string },
>(timeSpanEnum: T): Array<{ label: string; value: string }> {
  return [
    { label: TIME_SPAN_LABELS.day, value: timeSpanEnum.day },
    { label: TIME_SPAN_LABELS.week, value: timeSpanEnum.week },
    { label: TIME_SPAN_LABELS.month, value: timeSpanEnum.month },
    { label: TIME_SPAN_LABELS.custom, value: timeSpanEnum.custom },
  ];
}

export const TIME_SPAN_OPTIONS = createTimeSpanOptions(TimeSpan);

export function getStatusConfig(status: OrderStatusEnum) {
  return (
    ORDER_STATUS_CONFIG[status] || {
      label: status,
      color: "bg-gray-500/10 text-gray-500 border-gray-500/20",
    }
  );
}

function humanizePaymentMethod(raw: string): string {
  return raw
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getPaymentMethodLabel(method: PaymentMethod | string): string {
  if (method in PAYMENT_METHOD_LABELS) {
    return PAYMENT_METHOD_LABELS[method as PaymentMethod];
  }
  return humanizePaymentMethod(method);
}

export function getOrderTypeLabel(type: OrderType): string {
  return ORDER_TYPE_LABELS[type];
}

export const canSendReceipt = (provider?: ProviderEnum) => {
  if (!provider) return false;
  return [
    ProviderEnum.MunchiPos,
    ProviderEnum.MunchiOnline,
    ProviderEnum.MunchiKiosk,
  ].includes(provider as "MunchiOnline" | "MunchiKiosk" | "MunchiPos");
};
